import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import PageSkeleton from '../components/PageSkeleton';

const GREEN = '#6F9B41';

function profilePathForUserType(userType: string | undefined): string {
  if (userType === 'investor') return '/express-signup';
  if (userType === 'startup') return '/profile/startup';
  return '/dashboard';
}

/**
 * After pricing (free) or Stripe (paid): wait for the inbox click, then create password.
 * After password, if Auth email is still unverified, wait here until the link is clicked.
 */
const VerifyEmailPage: React.FC = () => {
  const { user, firebaseUser, emailVerified, loading, reloadFirebaseUser } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = (searchParams.get('t') || '').trim();
  const userId = (searchParams.get('userId') || '').trim();
  const [email, setEmail] = useState('');
  const [status, setStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(Boolean(token));
  const sentForUserRef = useRef<string | null>(null);
  const waitingForAuthVerify = Boolean(firebaseUser && !emailVerified && !token && !userId);

  useEffect(() => {
    if (token || userId) return;
    if (loading) return;
    if (!firebaseUser) {
      navigate('/login', { replace: true });
      return;
    }
    if (emailVerified) {
      navigate(profilePathForUserType(user?.userType), { replace: true });
    }
  }, [token, userId, loading, firebaseUser, emailVerified, user?.userType, navigate]);

  // Poll Auth while waiting for the post-password verify link to be clicked.
  useEffect(() => {
    if (!waitingForAuthVerify) return;
    const tick = async () => {
      try {
        await reloadFirebaseUser();
      } catch {
        /* keep waiting */
      }
    };
    void tick();
    const id = window.setInterval(() => void tick(), 3000);
    return () => window.clearInterval(id);
  }, [waitingForAuthVerify, reloadFirebaseUser]);

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    const confirm = async () => {
      setConfirming(true);
      setError('');
      try {
        const confirmFn = httpsCallable<
          { token: string },
          { success: boolean; userId?: string; nextPath?: string; error?: string }
        >(functions, 'confirmPreviewEmail');
        const result = await confirmFn({ token });
        if (cancelled) return;
        if (result.data?.success && result.data.userId) {
          // Prefer server unfinished-step path (password → express → dashboard).
          const next =
            typeof result.data.nextPath === 'string' && result.data.nextPath.startsWith('/')
              ? result.data.nextPath
              : `/finalstep/${result.data.userId}`;
          navigate(next, { replace: true });
          return;
        }
        setError(result.data?.error || 'This link is not valid.');
        setConfirming(false);
      } catch {
        if (!cancelled) {
          setError('Could not verify your email. The link may have expired.');
          setConfirming(false);
        }
      }
    };
    void confirm();
    return () => {
      cancelled = true;
    };
  }, [token, navigate]);

  useEffect(() => {
    if (token || !userId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const getUser = httpsCallable<
          { userId: string },
          { success: boolean; user?: { email?: string } }
        >(functions, 'getUser');
        const result = await getUser({ userId });
        if (cancelled) return;
        if (result.data?.success && result.data.user?.email) {
          setEmail(result.data.user.email);
        }
      } catch {
        /* waiting copy still works without the address */
      }
    };
    void load();
    return () => {
      cancelled = true;
    };
  }, [token, userId]);

  useEffect(() => {
    if (!waitingForAuthVerify) return;
    const fromAuth = firebaseUser?.email?.trim();
    if (fromAuth) setEmail(fromAuth);
  }, [waitingForAuthVerify, firebaseUser?.email]);

  const sendPreviewEmail = async () => {
    if (!userId) return;
    setStatus('sending');
    setError('');
    try {
      const sendFn = httpsCallable<
        { userId: string },
        { success: boolean; email?: string; error?: string }
      >(functions, 'sendPreviewVerificationEmail');
      const result = await sendFn({ userId });
      if (result.data?.success) {
        if (result.data.email) setEmail(result.data.email);
        setStatus('sent');
        sentForUserRef.current = userId;
        return;
      }
      setStatus('error');
      setError(result.data?.error || 'Could not send the verification email.');
    } catch {
      setStatus('error');
      setError('Could not send the verification email.');
    }
  };

  const resendAuthEmail = async () => {
    setStatus('sending');
    setError('');
    try {
      const sendFn = httpsCallable<{ email?: string }, { success?: boolean; error?: string }>(
        functions,
        'resendVerificationEmail'
      );
      const result = await sendFn({ email: email || firebaseUser?.email || undefined });
      if (result.data?.success) {
        setStatus('sent');
        return;
      }
      setStatus('error');
      setError(result.data?.error || 'Could not send the verification email.');
    } catch {
      setStatus('error');
      setError('Could not send the verification email.');
    }
  };

  useEffect(() => {
    if (token || !userId) return;
    if (sentForUserRef.current === userId) return;
    void sendPreviewEmail();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, userId]);

  if (!token && !userId && !waitingForAuthVerify) {
    return <PageSkeleton />;
  }

  if (confirming && !error) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-dark-950">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  const continueCopy = waitingForAuthVerify
    ? 'Open that email and click the button to continue to your profile.'
    : 'Open that email and click the button to continue creating your PRAIRI account.';

  return (
    <div className="flex min-h-screen items-center justify-center bg-dark-950 px-4 py-10">
      <div className="w-full max-w-[420px] rounded-[28px] border border-emerald-700/80 bg-[#1a1a1a] px-6 py-8 text-center text-white shadow-2xl sm:px-10 sm:py-10">
        <h1 className="mb-4 text-2xl font-bold tracking-tight sm:text-[28px]">Verify your email</h1>
        <p className="mb-2 text-sm text-white/90 sm:text-[15px]">
          We sent a link to{email ? ' ' : ' your inbox'}
          {email ? <span className="font-semibold"> {email}</span> : null}.
        </p>
        <p className="mb-8 text-sm text-white/80">{continueCopy}</p>
        {error ? (
          <p className="mb-4 text-sm text-red-400" role="alert">
            {error}
          </p>
        ) : null}
        {status === 'sent' && !error ? (
          <p className="mb-4 text-sm text-emerald-400">
            Verification email sent. Check your inbox.
          </p>
        ) : null}
        {userId ? (
          <button
            type="button"
            onClick={() => void sendPreviewEmail()}
            disabled={status === 'sending'}
            className="mb-4 w-full rounded-xl px-5 py-3 text-base font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: GREEN }}
          >
            {status === 'sending' ? 'Sending…' : 'Resend verification email'}
          </button>
        ) : null}
        {waitingForAuthVerify ? (
          <button
            type="button"
            onClick={() => void resendAuthEmail()}
            disabled={status === 'sending'}
            className="mb-4 w-full rounded-xl px-5 py-3 text-base font-semibold text-white disabled:opacity-50"
            style={{ backgroundColor: GREEN }}
          >
            {status === 'sending' ? 'Sending…' : 'Resend verification email'}
          </button>
        ) : null}
        <p className="text-sm text-white/80">
          Need help?{' '}
          <button
            type="button"
            onClick={() => navigate('/contact')}
            className="font-medium underline decoration-dotted underline-offset-4"
            style={{ color: GREEN }}
          >
            Contact our team.
          </button>
        </p>
      </div>
    </div>
  );
};

export default VerifyEmailPage;
